import type { MissedConversion, OpeningBookExit } from "@chessla/shared";
import type { PatternExample } from "@/features/patterns";

/** A Trends position that can open on the large practice board. */
export type TrendsPosition = { title: string; note: string; examples: PatternExample[] };

const side = (color: "white" | "black") => (color === "white" ? "w" : "b");
const pawns = (cp: number) => (cp === 0 ? "0.0" : `−${(cp / 100).toFixed(1)}`);

/** The costliest move while still winning; null when no single move lost the win (e.g. lost on the clock). */
export const missedConversionPosition = ({
  gameId,
  playedAt,
  result,
  playerColor,
  slip,
}: MissedConversion): TrendsPosition | null =>
  slip?.fen
    ? {
        title: `Missed win · ${playedAt.slice(0, 10)}`,
        note: `${result.toUpperCase()} · ${slip.phase.toUpperCase()}. Move ${slip.moveNumber}${slip.san ? ` ${slip.san}` : ""} at ${
          slip.evalBeforeMate != null
            ? `mate in ${Math.abs(slip.evalBeforeMate)}`
            : `+${((slip.evalBeforeCp ?? 0) / 100).toFixed(1)}`
        } cost ${(slip.lossCp / 100).toFixed(1)}${slip.bestMove ? ` · Best: ${slip.bestMove}` : ""}`,
        examples: [
          {
            gameId,
            moveNumber: slip.moveNumber,
            san: slip.san ?? "",
            fenBefore: slip.fen,
            bestMoveSan: slip.bestMove,
            evalBeforeCp: slip.evalBeforeCp,
            evalBeforeMate: slip.evalBeforeMate,
            playerColor: side(playerColor),
            playedAt,
          },
        ],
      }
    : null;

/** Where the player leaves theory, shown from the latest game so the recorded move is that game's move. */
export const bookExitPosition = (opening: string, color: "white" | "black", exit: OpeningBookExit): TrendsPosition => ({
  title: `${opening} · ${color}`,
  note: `You leave theory at move ${exit.moveNumber} (${exit.games} games). You played ${exit.played
    .map((move) => `${move.san} ×${move.games} (${pawns(move.averageLossCp)})`)
    .join(", ")}${exit.bestMove ? `. Best: ${exit.bestMove}.` : "."}`,
  examples: [
    {
      gameId: exit.latestGameId,
      moveNumber: exit.moveNumber,
      san: exit.latestSan,
      fenBefore: exit.fen,
      bestMoveSan: exit.bestMove,
      playerColor: side(color),
      opening,
    },
  ],
});
